import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import './Dashboard.css';
import TShirtOrderForm from './TShirtOrderForm';


function Dashboard({ user, onNavigate, onLogout }) {
  const [stats, setStats] = useState({
    totalStudents: 0, // ACTIVE students only
    totalOwing: 0, // ALL students (active + inactive)
    loading: true
  });

  const [franchiseeName, setFranchiseeName] = useState('');

  useEffect(() => {
  loadDashboardStats();
  loadPendingEnrollments();
}, []);

  const [pendingCount, setPendingCount] = useState(0);
  const [showTShirtOrder, setShowTShirtOrder] = useState(false);
  // Helper function: Calculate TERM FEES owing (excluding admin)
  const calculateTermFeesOwing = (student, termStructure) => {
    if (!student || !termStructure || !termStructure.termDates) return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const studentStartDate = student.startDate?.toDate 
      ? student.startDate.toDate() 
      : new Date(student.startDate);
    studentStartDate.setHours(0, 0, 0, 0);
    
    const currentYear = today.getFullYear();
    let totalTermFees = 0;

    const termFee = student.feeStructure?.standardFee || 0;
    const feeOverrides = student.feeOverrides || [];

    for (let termNum = 1; termNum <= 4; termNum++) {
      const termData = termStructure.termDates[`term${termNum}`];
      if (!termData || !termData.start || !termData.end) continue;

      const [startMonth, startDay] = termData.start.split('-').map(Number);
      const [endMonth, endDay] = termData.end.split('-').map(Number);
      
      const termStart = new Date(currentYear, startMonth - 1, startDay);
      termStart.setHours(0, 0, 0, 0);
      
      const termEnd = new Date(currentYear, endMonth - 1, endDay);
      termEnd.setHours(23, 59, 59, 999);

      const shouldCharge = (
        studentStartDate <= termEnd &&
        today >= termStart &&
        (student.isActive || 
          (student.deactivationInfo?.deactivatedDate && 
            new Date(student.deactivationInfo.deactivatedDate) > termStart))
      );

      if (shouldCharge) {
        // CHECK FOR OVERRIDE FIRST
        const override = feeOverrides.find(
          o => o.termNumber === termNum && 
          (o.overrideType === 'term' || o.overrideType === 'both')
        );

        if (override && override.termFee !== undefined) {
          // Use override amount
          totalTermFees += override.termFee;
        } else {
          // Use standard term fee
          totalTermFees += termFee;
        }
      }
    }

    const tp = student.termPayments || {};
const termFeesPaid = (tp.term1 || 0) + (tp.term2 || 0) + (tp.term3 || 0) + (tp.term4 || 0);
totalTermFees -= termFeesPaid;

    return Math.max(0, totalTermFees);
  };

  // Helper function: Calculate ADMIN FEE owing separately
  const calculateAdminFeeOwing = (student) => {
    const feeOverrides = student.feeOverrides || [];
    
    // Check if there's an admin fee override
    const adminOverride = feeOverrides.find(
      o => o.overrideType === 'admin' || o.overrideType === 'both'
    );

    let adminFee;
    if (adminOverride && adminOverride.adminFee !== undefined) {
      // Use override amount
      adminFee = adminOverride.adminFee;
    } else {
      // Use standard admin fee
      adminFee = student.adminFee || 0;
    }

    const adminFeePaid = student.termPayments?.admin || 0;
    return Math.max(0, adminFee - adminFeePaid);
  };

  const loadDashboardStats = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      
      // Fetch franchisee name
      const franchiseeDoc = await getDoc(franchiseeRef);
      if (franchiseeDoc.exists()) {
        const franchiseeData = franchiseeDoc.data();
        setFranchiseeName(franchiseeData.name || '');
      }
      
      // Get ACTIVE students only for count
      const activeStudentsQuery = query(
        collection(db, 'students'),
        where('franchiseeRef', '==', franchiseeRef),
        where('isActive', '==', true)
      );
      const activeStudentsSnapshot = await getDocs(activeStudentsQuery);
      const totalStudents = activeStudentsSnapshot.size;
      
      // Get ALL students for financial totals (active + inactive)
      const allStudentsQuery = query(
        collection(db, 'students'),
        where('franchiseeRef', '==', franchiseeRef)
      );
      const allStudentsSnapshot = await getDocs(allStudentsQuery);
      
      // Calculate real-time owing for each student
      let totalOwing = 0;
      
      for (const docSnap of allStudentsSnapshot.docs) {
        const student = docSnap.data();
        
        // Try to calculate real-time owing
        if (student.schoolRef) {
          try {
            const schoolDoc = await getDoc(student.schoolRef);
            if (schoolDoc.exists()) {
              const schoolData = schoolDoc.data();
              
              if (schoolData.termStructureRef) {
                const termStructureDoc = await getDoc(schoolData.termStructureRef);
                if (termStructureDoc.exists()) {
                  const termStructure = termStructureDoc.data();
                  
                  // Calculate separate amounts
                  const termFeesOwing = calculateTermFeesOwing(student, termStructure);
                  const adminFeeOwing = calculateAdminFeeOwing(student);
                  
                  totalOwing += (termFeesOwing + adminFeeOwing);
                  continue;
                }
              }
            }
          } catch (error) {
            console.error('Error calculating owing for student:', student.studentName, error);
          }
        }
        
        // Fallback: use stored values if calculation fails
        const termFeesOwing = student.termFeesOwing || 0;
        const adminFeeOwing = calculateAdminFeeOwing(student);
        totalOwing += (termFeesOwing + adminFeeOwing);
      }
      
      setStats({
        totalStudents, // Active students only
        totalOwing, // All students - calculated in real-time
        loading: false
      });
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      setStats({ totalStudents: 0, totalOwing: 0, loading: false });
    }
  };
  const loadPendingEnrollments = async () => {
  try {
    const user = auth.currentUser;
    const q = query(
      collection(db, 'pendingEnrollments'),
      where('franchiseeId', '==', user.uid),
      where('status', '==', 'pending')
    );
    
    const snapshot = await getDocs(q);
    setPendingCount(snapshot.size);
  } catch (error) {
    console.error('Error loading pending enrollments:', error);
  }
};

  return (
    <div className="dashboard-container">
      {/* Top App Bar */}
      <div className="dashboard-appbar">
        <div className="appbar-left">
          <img src="/arty.png" alt="BABS Logo" className="appbar-logo" />
          <div className="appbar-branding">
            <h1 className="appbar-title">BABS</h1>
            <p className="appbar-subtitle">Beaux-Arts Bookkeeping System</p>
          </div>
        </div>
        
        <div className="appbar-right">
          <div className="user-badge">
            <span className="user-icon">👤</span>
            <span className="user-email">{user.email}</span>
          </div>
          <button onClick={onLogout} className="logout-btn" title="Logout">
  Log Out
</button>
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-main">
        {/* Welcome Header */}
        <div className="welcome-header">
          <h2 className="welcome-title">
            Welcome{franchiseeName && `, ${franchiseeName}`}
          </h2>
          <p className="welcome-subtitle">Select a category below to manage your business</p>
        </div>

        {/* Stats Cards */}
        <div className="stats-container">
          <div className="stat-card stat-students">
            <div className="stat-icon">👨‍🎓</div>
            <div className="stat-info">
              <div className="stat-label">Active Students</div>
              <div className="stat-value">
                {stats.loading ? '...' : stats.totalStudents}
              </div>
            </div>
          </div>

          <div className="stat-card stat-owing">
            <div className="stat-icon">💰</div>
            <div className="stat-info">
              <div className="stat-label">Outstanding Fees</div>
              <div className="stat-value">
                {stats.loading ? '...' : `R${stats.totalOwing.toFixed(2)}`}
              </div>
            </div>
          </div>
          <div className="stat-card stat-pending">
  <div className="stat-icon">📋</div>
  <div className="stat-info">
    <div className="stat-label">Online Enrollments</div>
    <div className="stat-value">
      {stats.loading ? '...' : pendingCount}
    </div>
  </div>
</div>
        </div>

        {/* Category Grid - 4 Main Categories */}
        <div className="category-grid">
           <button 
            className="category-card card-purple" 
            onClick={() => {
              console.log('Navigating to setupCategory');
              onNavigate('setupCategory');
            }}
          >
            <span className="category-icon">⚙️</span>
            <span className="category-title">Setup &</span>
            <span className="category-subtitle">Configuration</span>
          </button>
          <button 
            className="category-card card-blue" 
            onClick={() => {
              console.log('Navigating to schoolStudentCategory');
              onNavigate('schoolStudentCategory');
            }}
          >
            <span className="category-icon">🏫</span>
            <span className="category-title">School & Student</span>
            <span className="category-subtitle">Management</span>
          </button>
          
         

          <button 
            className="category-card card-green" 
            onClick={() => {
              console.log('Navigating to financialCategory');
              onNavigate('financialCategory');
            }}
          >
            <span className="category-icon">💰</span>
            <span className="category-title">Financial</span>
            <span className="category-subtitle">Operations</span>
          </button>
          
          <button 
            className="category-card card-orange" 
            onClick={() => {
              console.log('Navigating to reportsCategory');
              onNavigate('reportsCategory');
            }}
          >
            <span className="category-icon">📊</span>
            <span className="category-title">Reports</span>
          
          </button>
          <button 
  className="category-card card-yellow" 
  onClick={() => onNavigate('pendingEnrollments')}
>
  <span className="category-icon">📋</span>
  <span className="category-title">Online</span>
  <span className="category-subtitle">Enrollments</span>
</button>
<button 
  className="category-card card-teal" 
  onClick={() => setShowTShirtOrder(true)}
>
  <span className="category-icon">👕</span>
  <span className="category-title">Order T-Shirts</span>
  <span className="category-subtitle">& Supplies</span>
</button>

        </div>
        
      </div>
      
      {/* T-Shirt Order Modal */}
      {showTShirtOrder && (
        <TShirtOrderForm 
          onClose={() => setShowTShirtOrder(false)}
          onOrderPlaced={() => {
            setShowTShirtOrder(false);
            loadDashboardStats();
          }}
        />
      )}
      
    </div>  
  );
}

export default Dashboard;